<template>
  <div class="mx-auto my-2">
    <div v-if="!showTable" class="import-entry-shell">
      <v-card class="import-entry-card" flat>
        <div class="import-entry-card__header">
          <div>
            <div class="import-entry-card__eyebrow">Import Helper</div>
            <div class="import-entry-card__title">
              {{ categoryLabel }} Import
            </div>
            <div class="import-entry-card__subtitle">
              Download the latest template first, keep the same columns, then
              upload the completed file for validation.
            </div>
          </div>
          <div class="import-entry-card__actions">
            <v-btn
 v-if="$can((preServiceLevel === 'TVETProgram' ? 'tvet' : 'graduate-entry'), 'downloadTemplate')"              :disabled="lookupLoading"
              color="primary"
              depressed
              rounded
              @click="downloadStructuredTemplate"
            >
              <v-icon left>mdi-file-download-outline</v-icon>
              <span>Download Template</span>
            </v-btn>
            <v-btn color="primary" outlined rounded @click="goBack()">
              <v-icon left>mdi-arrow-left</v-icon>
              <span>Back to Records</span>
            </v-btn>
          </div>
        </div>
        <div class="import-entry-card__steps">
          <div class="import-entry-card__step">
            <span class="import-entry-card__step-number">1</span>
            <span
              >Download the latest {{ categoryLabel.toLowerCase() }} template
              generated from current lookups.</span
            >
          </div>
          <div class="import-entry-card__step">
            <span class="import-entry-card__step-number">2</span>
            <span
              >Fill the spreadsheet without renaming or removing the
              headers.</span
            >
          </div>
          <div class="import-entry-card__step">
            <span class="import-entry-card__step-number">3</span>
            <span
              >Upload the file and review the validation summary before
              import.</span
            >
          </div>
        </div>

        <div v-if="!showUpload" class="import-entry-card__uploader">
          <upload-excel-component
            :before-upload="beforeUpload"
            :on-success="handleSuccess"
          />
        </div>
      </v-card>
    </div>

    <graduate-import-review
      v-if="showUpload"
      :file-name="fileName"
      :loading="loading"
      :row-count="tableData.length"
      :action-label="`Import ${categoryLabel} Rows`"
      :title="`${categoryLabel} spreadsheet validated`"
      @cancel="goBack"
      @replace="replaceImportFile"
      @upload="uploadGraduates"
    />

    <v-card v-if="showTable" class="mx-2 preservice-list-card" flat>
      <div class="filter-toolbar">
        <div class="filter-toolbar__header">
          <div>
            <div class="filter-toolbar__eyebrow">Pre-service Filters</div>
            <div class="filter-toolbar__title">{{ categoryLabel }}</div>
          </div>
          <div class="filter-toolbar__actions">
            <v-btn
              class="filter-toolbar__clear-button"
              color="primary"
              outlined
              rounded
              @click="clearFilters"
            >
              <v-icon left>mdi-filter-remove-outline</v-icon>
              <span>Clear Filters</span>
            </v-btn>
            <v-btn
              v-if="$can((preServiceLevel === 'TVETProgram' ? 'tvet' : 'graduate-entry'), 'import', (canCreatePService))"
              class="filter-toolbar__secondary-button"
              color="primary"
              depressed
              rounded
              @click="newImport"
            >
              <v-icon left>mdi-import</v-icon>
              <span>New Import</span>
            </v-btn>
            <v-btn
              v-if="canCreatePService"
              class="filter-toolbar__primary-button"
              color="success"
              depressed
              rounded
              @click="addNimeiProgram"
            >
              <v-icon left>mdi-plus-circle</v-icon>
              <span>Add</span>
            </v-btn>
          </div>
        </div>
        <v-row class="filter-toolbar__fields" dense>
          <v-col cols="12" sm="6" md="4" lg="3">
            <v-text-field
              v-model="name"
              class="filter-toolbar__field"
              dense
              hide-details
              label="Search by Name"
              outlined
              prepend-inner-icon="mdi-account-search-outline"
              rounded
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="3">
            <v-text-field
              v-model="idValue"
              class="filter-toolbar__field"
              dense
              hide-details
              label="National ID (FAN)"
              outlined
              prepend-inner-icon="mdi-card-account-details-outline"
              rounded
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="2">
            <v-autocomplete
              v-model="programValue"
              :clearable="clearable"
              :items="EDUCATIONAL_PROGRAMS"
              :loading="lookupLoading"
              class="filter-toolbar__field"
              dense
              item-text="name"
              item-value="id"
              :label="programLabel"
              outlined
              prepend-inner-icon="mdi-shape-outline"
              return-object
              rounded
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" md="6" lg="2">
            <v-autocomplete
              v-model="disciplineValue"
              :clearable="clearable"
              :items="DISCIPLINES"
              :loading="lookupLoading"
              class="filter-toolbar__field"
              dense
              item-text="name"
              item-value="id"
              label="Department / Discipline"
              outlined
              prepend-inner-icon="mdi-stethoscope"
              return-object
              rounded
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" md="6" lg="2">
            <v-autocomplete
              v-model="levelOfStudyValue"
              :clearable="clearable"
              :items="LEVEL_OF_STUDIES"
              :loading="lookupLoading"
              class="filter-toolbar__field"
              dense
              item-text="name"
              item-value="id"
              label="Level Of Study"
              outlined
              prepend-inner-icon="mdi-school-outline"
              return-object
              rounded
            ></v-autocomplete>
          </v-col>
        </v-row>
      </div>
      <v-data-table
        :headers="headers"
        :items="tableData.rows"
        :loading="loading"
        :options.sync="options"
        :server-items-length="tableData.count"
        class="elevation-1"
        loading-text="Loading... please wait"
      >
        <template v-slot:[`item.fullName`]="{ item }">
          {{ item.fullName ? item.fullName : "-" }}
        </template>
        <template v-slot:[`item.sex`]="{ item }">
          {{ item.sex ? item.sex : "-" }}
        </template>
        <template v-slot:[`item.idNo`]="{ item }">
          {{ item.idNo ? formatFanId(item.idNo) : "-" }}
        </template>
        <template v-slot:[`item.backgroundProfession`]="{ item }">
          {{ item.backgroundProfession ? item.backgroundProfession : "-" }}
        </template>
        <template v-slot:[`item.entryYear`]="{ item }">
          {{ item.entryYear ? item.entryYear : "-" }}
        </template>
        <template v-slot:[`item.levelOfEducation`]="{ item }">
          {{ item.levelOfEducation ? item.levelOfEducation : "-" }}
        </template>
        <template v-slot:[`item.expectedYearOfGraduation`]="{ item }">
          {{
            item.expectedYearOfGraduation ? item.expectedYearOfGraduation : "-"
          }}
        </template>
        <template v-slot:[`item.higherLearningInstitution`]="{ item }">
          {{
            item.higherLearningInstitution
              ? item.higherLearningInstitution
              : "-"
          }}
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon
                class="preservice-record-action preservice-record-action--view mr-2"
                @click="viewImportedGraduates(item)"
                v-on="on"
                >mdi-eye
              </v-icon>
            </template>
            <span>View {{ categoryLabel }} detail</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-if="canEditPService" v-slot:activator="{ on }">
              <v-icon
                class="preservice-record-action preservice-record-action--edit mr-2"
                @click="editNimeiProgram(item)"
                v-on="on"
                >mdi-circle-edit-outline
              </v-icon>
            </template>
            <span>Edit {{ categoryLabel }}</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <template>
      <v-dialog v-model="detailDialog" persistent width="60%">
        <v-card class="preservice-dialog-card">
          <v-card-title class="preservice-dialog-header">
            <div class="preservice-dialog-header__icon">
              <v-icon color="white">mdi-account-details-outline</v-icon>
            </div>
            <div class="preservice-dialog-header__content">
              <div class="preservice-dialog-header__title">{{ title }}</div>
              <div class="preservice-dialog-header__subtitle">
                Review identity, education, institution, and grade information.
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-btn
              aria-label="Close student detail dialog"
              class="preservice-dialog-close"
              icon
              @click="closeDialog()"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <div class="preservice-dialog-body">
            <NimeiProgramDetail
              v-if="detailDialog"
              :graduates="graduates"
              @close="detailDialog = false"
            ></NimeiProgramDetail>
          </div>
        </v-card>
      </v-dialog>
    </template>

    <template>
      <v-dialog v-model="addDialog" persistent width="60%">
        <v-card class="preservice-dialog-card">
          <v-card-title class="preservice-dialog-header">
            <div class="preservice-dialog-header__icon">
              <v-icon color="white">
                {{ graduates ? "mdi-account-edit-outline" : "mdi-account-plus-outline" }}
              </v-icon>
            </div>
            <div class="preservice-dialog-header__content">
              <div class="preservice-dialog-header__title">{{ title }}</div>
              <div class="preservice-dialog-header__subtitle">
                {{
                  graduates
                    ? "Update this student's pre-service record."
                    : "Create a new pre-service student record."
                }}
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-btn
              aria-label="Close create or edit student dialog"
              class="preservice-dialog-close"
              icon
              @click="closeDialog()"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <div class="preservice-dialog-body">
            <HrdAddNimeiProgram
              v-if="addDialog"
              :graduates="graduates"
              :level-type="levelType"
              :pre-service-level="preServiceLevel"
              :submitting="saving"
              @close="addDialog = false"
              @registerOrUpdatePreService="registerOrUpdatePreService"
            ></HrdAddNimeiProgram>
          </div>
        </v-card>
      </v-dialog>
      <importErrorModal
        :dialogState="importErrorsDialog"
        :importErrors="importErrors"
        @dialogEvent="() => (importErrorsDialog = false)"
      ></importErrorModal>
    </template>
  </div>
</template>

<script>
import { GraduateService } from "../graduates.service";
import UploadExcelComponent from "../UploadExcel.vue";
import NimeiProgramDetail from "./nimei-program-detail.vue";
import HrdAddNimeiProgram from "./add-nimei-program.vue";
import {
  getDisciplines,
  getEducationalPrograms,
  getEthnicities,
  getHealthProfessions,
  getLevelOfStudies,
  getRegionList,
  getStudentStatuses,
  getUser,
  getUniversities,
  getWoredaList,
  getZoneList,
  getAssignedUniversityOption,
  isUniversityScopedPreserviceUser,
} from "@/utils/helper";
import importErrorModal from "../importErrorModal.vue";
import GraduateImportReview from "@/components/Graduates/GraduateImportReview.vue";
import {
  buildGraduateEntryTemplateOptions,
  buildTvetTemplateOptions,
  DETAILED_STRUCTURED_TEMPLATE_HEADERS,
  filterGraduateEntryLevelOfStudies,
  formatAllowedValues,
  LEGACY_DETAILED_STRUCTURED_TEMPLATE_HEADERS,
  LEGACY_TVET_TEMPLATE_HEADERS,
  PRESERVICE_STATUS_SHEET_NAME,
  resolveGraduateEntryBackgroundProfession,
  TVET_BACKGROUND_PROFESSION_OPTIONS,
  TVET_PRESERVICE_LEVEL,
  TVET_STUDENT_STATUS_OPTIONS,
  TVET_TEMPLATE_HEADERS,
  TVET_YEAR_OF_EDUCATION_OPTIONS,
  validatePreserviceImportSource,
  validateStructuredPreServiceImport,
} from "@/components/Graduates/graduate-import-utils";
import { downloadStructuredTemplateWorkbook } from "@/components/Graduates/structured-template-workbook";
import { formatFanId, normalizeFanId } from "@/utils/fan-id";
import { YearOfEducEnum } from "@/utils/enums";
const ExcelJS = require("exceljs");
const { saveAs } = require("file-saver");

export default {
  props: {
    categoryLabel: {
      type: String,
      default: "Graduate Entry",
    },
    preServiceLevel: {
      type: String,
      default: "NimeiProgram",
    },
    levelType: {
      type: String,
      default: "NIMEI Program",
    },
  },
  components: {
    UploadExcelComponent,
    NimeiProgramDetail,
    HrdAddNimeiProgram,
    importErrorModal,
    GraduateImportReview,
  },
  data() {
    return {
      fileName: "",
      showTable: true,
      showUpload: false,
      showUploadExcel: false,
      loading: false,
      saving: false,
      search: "",
      importErrorsDialog: false,
      importErrors: [],
      headers: [
        { text: "Full Name", value: "fullName" },
        { text: "Sex", value: "sex" },
        { text: "National ID (FAN)", value: "idNo" },
        { text: "Background Profession", value: "backgroundProfession" },
        { text: "Year of Entry", value: "entryYear" },
        { text: "Level Of Study", value: "levelOfStudy.name" },
        {
          text: "Expected Graduation Year",
          value: "expectedYear",
        },
        {
          text: "Higher Learning Institution",
          value: "university.name",
        },
        { text: "Actions", value: "actions", sortable: false, align: "center" },
      ],
      tableData: [],
      detailDialog: false,
      title: "",
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      addDialog: false,

      lookupLoading: false,
      LEVEL_OF_STUDIES: [],
      EDUCATIONAL_PROGRAMS: [],
      EDUC_LEVEL: [],
      BACKGROUND_PROFESSIONS: [],
      STUDENT_STATUSES: [],
      YEAR_OF_EDUCATION: [],
      DISCIPLINES: [],
      allDisciplines: [],
      ETHNICITIES: [],
      BIRTH_REGIONS: [],
      BIRTH_ZONES: [],
      BIRTH_WOREDAS: [],
      UNIVERSITIES: [],

      programValue: null,
      disciplineValue: null,
      departmentValue: null,
      levelOfStudyValue: null,
      idValue: null,
      name: null,
      clearable: true,
    };
  },
  async beforeMount() {
    this.fetchImportedGraduates();
    this.loadLookups();
  },
  watch: {
    options: {
      handler() {
        this.fetchImportedGraduates();
      },
      deep: true,
    },
    name: function (val) {
      this.name = val;
      this.fetchImportedGraduates();
    },
    idValue: function (val) {
      this.idValue = formatFanId(val);
      this.fetchImportedGraduates();
    },
    programValue: function (val) {
      this.programValue = val == "" ? null : val;
      this.fetchOnFilters();
      this.fetchImportedGraduates();
    },
    disciplineValue: function (val) {
      this.disciplineValue = val == "" ? null : val;
      this.departmentValue = val?.name || null;
      this.fetchImportedGraduates();
    },
    levelOfStudyValue: function (val) {
      this.levelOfStudyValue = val == "" ? null : val;
      this.fetchImportedGraduates();
    },
  },
  computed: {
    isTvetProgram() {
      return this.preServiceLevel === TVET_PRESERVICE_LEVEL;
    },
    isGraduateEntryProgram() {
      return this.preServiceLevel === "NimeiProgram";
    },
    supportsDynamicTemplate() {
      return this.isTvetProgram || this.isGraduateEntryProgram;
    },
    programLabel() {
      return "Program Education Type";
    },
    canCreatePService() {
      return getUser().permissions.some(
        (permission) =>
          permission.menuItem.menuItemName == "Pre-service" &&
          permission.canCreate,
      );
    },
    canEditPService() {
      return getUser().permissions.some(
        (permission) =>
          permission.menuItem.menuItemName == "Pre-service" &&
          permission.canUpdate,
      );
    },
  },
  methods: {
    formatFanId,
    fetchOnFilters() {
      if (!this.programValue?.id) {
        this.DISCIPLINES = this.allDisciplines;
        return;
      }
      this.DISCIPLINES = this.allDisciplines.filter(
        (item) => item?.educationProgramId == this.programValue.id,
      );
      if (
        this.disciplineValue &&
        !this.DISCIPLINES.some((item) => item?.id === this.disciplineValue.id)
      ) {
        this.disciplineValue = null;
        this.departmentValue = null;
      }
    },
    async fetchImportedGraduates() {
      this.loading = true;
      const { page, itemsPerPage } = this.options;

      let searchParams = `&preServiceLevel=${this.preServiceLevel}`;
      this.name !== null && (searchParams += "&name=" + this.name);
      this.idValue !== null &&
        (searchParams += "&idNo=" + normalizeFanId(this.idValue));
      this.programValue !== null &&
        (searchParams += "&educationProgramId=" + this.programValue.id);
      this.departmentValue !== null &&
        (searchParams += "&department=" + this.departmentValue);
      this.levelOfStudyValue !== null &&
        (searchParams += "&levelOfStudyId=" + this.levelOfStudyValue.id);

      let res = await GraduateService.getPostServices(
        page,
        itemsPerPage,
        searchParams,
      );
      if (res.status === 200) {
        this.loading = false;
        this.showTable = true;
        return (this.tableData = res.data);
      }
    },
    async loadLookups() {
      this.lookupLoading = true;
      try {
        const levelOfStudies = await getLevelOfStudies();
        const levelRows = levelOfStudies?.rows || [];
        this.LEVEL_OF_STUDIES = this.isGraduateEntryProgram
          ? filterGraduateEntryLevelOfStudies(levelRows)
          : levelRows.filter((item) => item?.level_type == this.levelType);
        const lookupScope = this.isGraduateEntryProgram
          ? "postgraduate"
          : "standard";
        this.EDUCATIONAL_PROGRAMS = await getEducationalPrograms({
          scope: lookupScope,
        });
        this.EDUCATIONAL_PROGRAMS = this.EDUCATIONAL_PROGRAMS.rows;
        const disciplines = await getDisciplines({ scope: lookupScope });
        this.allDisciplines = disciplines?.rows || [];
        this.DISCIPLINES = this.allDisciplines;
        this.ETHNICITIES = (await getEthnicities())?.rows || [];
        this.BIRTH_REGIONS = (await getRegionList()) || [];
        this.BIRTH_ZONES = (await getZoneList()) || [];
        this.BIRTH_WOREDAS = (await getWoredaList()) || [];
        const universityResponse = await getUniversities();
        const activeUniversities = (universityResponse?.rows || []).filter(
          (item) => item?.isActive !== false,
        );
        const assignedUniversity = getAssignedUniversityOption();
        this.UNIVERSITIES =
          isUniversityScopedPreserviceUser() && assignedUniversity
            ? [assignedUniversity]
            : activeUniversities;
        if (this.supportsDynamicTemplate) {
          this.YEAR_OF_EDUCATION = this.isTvetProgram
            ? TVET_YEAR_OF_EDUCATION_OPTIONS.map((name) => ({ name }))
            : [{ name: "Not Applicable", code: "NA" }].concat(
                YearOfEducEnum,
              );
        }
        if (this.isTvetProgram) {
          this.BACKGROUND_PROFESSIONS = TVET_BACKGROUND_PROFESSION_OPTIONS.map(
            (name) => ({ name }),
          );
          this.STUDENT_STATUSES = TVET_STUDENT_STATUS_OPTIONS.map((name) => ({
            name,
          }));
        } else {
          const healthProfessions = await getHealthProfessions();
          const activeProfessions = (healthProfessions?.rows || []).filter(
            (data) => data?.isActive === true,
          );
          if (this.isGraduateEntryProgram) {
            this.BACKGROUND_PROFESSIONS =
              resolveGraduateEntryBackgroundProfession(activeProfessions).options;
            const studentStatuses = await getStudentStatuses();
            this.STUDENT_STATUSES = studentStatuses?.rows || studentStatuses || [];
          } else {
            this.BACKGROUND_PROFESSIONS = activeProfessions;
          }
        }
      } catch (error) {
        console.error(error);
        this.$toast.error(
          `Unable to load the ${this.categoryLabel} form lookups. Please retry.`,
        );
      } finally {
        this.lookupLoading = false;
      }
    },
    buildGraduateEntryWorkbookTemplateOptions() {
      return buildGraduateEntryTemplateOptions({
        lookups: {
          backgroundProfessions: this.BACKGROUND_PROFESSIONS,
          disciplines: this.allDisciplines,
          educationalPrograms: this.EDUCATIONAL_PROGRAMS,
          ethnicities: this.ETHNICITIES,
          birthRegions: this.BIRTH_REGIONS,
          birthZones: this.BIRTH_ZONES,
          birthWoredas: this.BIRTH_WOREDAS,
          levelOfStudies: this.LEVEL_OF_STUDIES,
          studentStatuses: this.STUDENT_STATUSES,
          yearOfEducation: this.YEAR_OF_EDUCATION,
          universities: this.UNIVERSITIES,
        },
      });
    },
    buildTvetWorkbookTemplateOptions() {
      return buildTvetTemplateOptions({
        lookups: {
          backgroundProfessions: this.BACKGROUND_PROFESSIONS,
          disciplines: this.allDisciplines,
          educationalPrograms: this.EDUCATIONAL_PROGRAMS,
          ethnicities: this.ETHNICITIES,
          birthRegions: this.BIRTH_REGIONS,
          birthZones: this.BIRTH_ZONES,
          birthWoredas: this.BIRTH_WOREDAS,
          levelOfStudies: this.LEVEL_OF_STUDIES,
          studentStatuses: this.STUDENT_STATUSES,
          yearOfEducation: this.YEAR_OF_EDUCATION,
          universities: this.UNIVERSITIES,
        },
      });
    },
    async downloadStructuredTemplate() {
      const templateOptions = this.isGraduateEntryProgram
        ? this.buildGraduateEntryWorkbookTemplateOptions()
        : this.buildTvetWorkbookTemplateOptions();
      if (
        !Object.values(templateOptions.missingRequirements || {}).every(Boolean)
      ) {
        this.$toast.error(
          `Unable to generate the ${this.categoryLabel} template because one or more lookups are missing.`,
        );
        return;
      }

      const fileName = this.isGraduateEntryProgram
        ? "Graduate_Entry_Template.xlsx"
        : "TVET_Program_Template.xlsx";
      await downloadStructuredTemplateWorkbook({
        categoryLabel: this.categoryLabel,
        fileName,
        requiresGpa: true,
        templateOptions,
      });
      this.$toast.success(`${fileName} downloaded.`);
    },
    getColumnLetter(index) {
      let dividend = index;
      let columnName = "";
      while (dividend > 0) {
        const modulo = (dividend - 1) % 26;
        columnName = String.fromCharCode(65 + modulo) + columnName;
        dividend = Math.floor((dividend - modulo) / 26);
      }
      return columnName;
    },
    async downloadStructuredTemplateLegacy() {
      const templateOptions = this.isGraduateEntryProgram
        ? this.buildGraduateEntryWorkbookTemplateOptions()
        : this.buildTvetWorkbookTemplateOptions();
      if (
        !Object.values(templateOptions.missingRequirements || {}).every(Boolean)
      ) {
        this.$toast.error(
          `Unable to generate the ${this.categoryLabel} template because one or more lookups are missing.`,
        );
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const templateSheet = workbook.addWorksheet(
        `${this.categoryLabel} Template`,
      );
      const instructionSheet = workbook.addWorksheet("Instructions");
      const lookupSheet = workbook.addWorksheet("Lookups");
      lookupSheet.state = "veryHidden";
      const currentYear = new Date().getFullYear();
      const columnMap = templateOptions.headers.reduce(
        (accumulator, header, index) => {
          accumulator[header] = this.getColumnLetter(index + 1);
          return accumulator;
        },
        {},
      );
      const lastColumnLetter = this.getColumnLetter(
        templateOptions.headers.length,
      );

      instructionSheet.addRow([`${this.categoryLabel} import template`]);
      instructionSheet.addRow([
        `1. Use the '${this.categoryLabel} Template' sheet only.`,
      ]);
      instructionSheet.addRow([
        "2. Use dropdown columns for Sex, Background Profession, Program Education Type, Department / Discipline, Level Of Study, Year of Education, and Student Status.",
      ]);
      instructionSheet.addRow([
        `3. Year of Entry must be between 1950 and ${currentYear}.`,
      ]);
      instructionSheet.addRow(["4. Expected Graduation Year is required."]);
      instructionSheet.addRow([
        "5. Year of Graduation is optional, but if provided it cannot be earlier than Expected Graduation Year.",
      ]);
      instructionSheet.addRow([
        "6. National ID (FAN) must contain exactly 16 digits.",
      ]);
      instructionSheet.addRow([
        this.isGraduateEntryProgram
          ? "7. GPA and Cumulative GPA must be between 0 and 4."
          : "7. Cumulative GPA must be between 0 and 4.",
      ]);
      instructionSheet.getColumn(1).width = 120;

      templateSheet.addRow(templateOptions.headers);
      templateSheet.views = [{ state: "frozen", ySplit: 1 }];
      templateSheet.autoFilter = {
        from: "A1",
        to: `${lastColumnLetter}1`,
      };
      [18, 18, 22, 12, 22, 24, 24, 28, 20, 14, 24, 20, 20, 18, 14, 18].forEach(
        (width, index) => {
          if (index >= templateOptions.headers.length) {
            return;
          }
          templateSheet.getColumn(index + 1).width = width;
        },
      );
      templateSheet.getColumn(columnMap["National ID (FAN)"]).numFmt = "@";

      const lookupGroups = [
        { header: "Sex", values: templateOptions.sexOptions },
        {
          header: "Background Profession",
          values: templateOptions.backgroundProfessionOptions,
        },
        {
          header: "Program Education Type",
          values:
            templateOptions.programEducationTypeOptions ||
            templateOptions.programOptions,
        },
        {
          header: "Department / Discipline",
          values: templateOptions.disciplineOptions,
        },
        {
          header: "Level Of Study",
          values: templateOptions.levelOfStudyOptions,
        },
        {
          header: "Year of Education",
          values: templateOptions.yearOfEducationOptions,
        },
        {
          header: "Student Status",
          values: templateOptions.studentStatusOptions,
        },
      ];

      lookupSheet.addRow(lookupGroups.map((group) => group.header));
      const lookupHeight = Math.max(
        ...lookupGroups.map((group) => group.values.length),
      );
      for (let rowIndex = 0; rowIndex < lookupHeight; rowIndex += 1) {
        lookupSheet.addRow(
          lookupGroups.map((group) => group.values[rowIndex] || ""),
        );
      }

      const listFormulaByHeader = lookupGroups.reduce(
        (accumulator, group, index) => {
          const columnLetter = this.getColumnLetter(index + 1);
          accumulator[group.header] =
            `Lookups!$${columnLetter}$2:$${columnLetter}$${group.values.length + 1}`;
          return accumulator;
        },
        {},
      );

      for (let rowNumber = 2; rowNumber <= 201; rowNumber += 1) {
        templateSheet.getCell(
          `${columnMap["Sex"]}${rowNumber}`,
        ).dataValidation = {
          type: "list",
          allowBlank: false,
          formulae: [listFormulaByHeader["Sex"]],
          showErrorMessage: true,
          errorTitle: "Invalid Sex",
          error: "Select Male or Female from the dropdown.",
        };
        templateSheet.getCell(
          `${columnMap["National ID (FAN)"]}${rowNumber}`,
        ).numFmt = "@";
        templateSheet.getCell(
          `${columnMap["National ID (FAN)"]}${rowNumber}`,
        ).dataValidation = {
          type: "textLength",
          operator: "equal",
          allowBlank: false,
          formulae: [16],
          showErrorMessage: true,
          errorTitle: "Invalid National ID",
          error:
            "National ID (FAN) must be exactly 16 digits and should be entered in a Text cell.",
        };
        templateSheet.getCell(
          `${columnMap["Background Profession"]}${rowNumber}`,
        ).dataValidation = {
          type: "list",
          allowBlank: false,
          formulae: [listFormulaByHeader["Background Profession"]],
          showErrorMessage: true,
          errorTitle: "Invalid Background Profession",
          error: `Select one of: ${formatAllowedValues(templateOptions.backgroundProfessionOptions)}.`,
        };
        templateSheet.getCell(
          `${columnMap["Program Education Type"]}${rowNumber}`,
        ).dataValidation = {
          type: "list",
          allowBlank: false,
          formulae: [listFormulaByHeader["Program Education Type"]],
          showErrorMessage: true,
          errorTitle: "Invalid Program Education Type",
          error: `Select one of: ${formatAllowedValues(templateOptions.programEducationTypeOptions || templateOptions.programOptions)}.`,
        };
        templateSheet.getCell(
          `${columnMap["Department / Discipline"]}${rowNumber}`,
        ).dataValidation = {
          type: "list",
          allowBlank: false,
          formulae: [listFormulaByHeader["Department / Discipline"]],
          showErrorMessage: true,
          errorTitle: "Invalid Discipline",
          error: "Select a discipline from the dropdown list.",
        };
        templateSheet.getCell(
          `${columnMap["Level Of Study"]}${rowNumber}`,
        ).dataValidation = {
          type: "list",
          allowBlank: false,
          formulae: [listFormulaByHeader["Level Of Study"]],
          showErrorMessage: true,
          errorTitle: "Invalid Level Of Study",
          error: "Select a level of study from the dropdown list.",
        };
        templateSheet.getCell(
          `${columnMap["Year of Entry"]}${rowNumber}`,
        ).dataValidation = {
          type: "whole",
          operator: "between",
          allowBlank: false,
          formulae: [1950, currentYear],
          showErrorMessage: true,
          errorTitle: "Invalid Entry Year",
          error: `Year of Entry must be between 1950 and ${currentYear}.`,
        };
        templateSheet.getCell(
          `${columnMap["Expected Graduation Year"]}${rowNumber}`,
        ).dataValidation = {
          type: "whole",
          operator: "between",
          allowBlank: false,
          formulae: [1950, 9999],
          showErrorMessage: true,
          errorTitle: "Invalid Expected Graduation Year",
          error: "Expected Graduation Year must be a valid 4-digit year.",
        };
        templateSheet.getCell(
          `${columnMap["Year of Graduation"]}${rowNumber}`,
        ).dataValidation = {
          type: "whole",
          operator: "between",
          allowBlank: true,
          formulae: [1950, 9999],
          showErrorMessage: true,
          errorTitle: "Invalid Graduation Year",
          error: "Year of Graduation must be a valid 4-digit year.",
        };
        templateSheet.getCell(
          `${columnMap["Year of Education"]}${rowNumber}`,
        ).dataValidation = {
          type: "list",
          allowBlank: false,
          formulae: [listFormulaByHeader["Year of Education"]],
          showErrorMessage: true,
          errorTitle: "Invalid Year Of Education",
          error: "Select a Year of Education value from the dropdown list.",
        };
        templateSheet.getCell(
          `${columnMap["Student Status"]}${rowNumber}`,
        ).dataValidation = {
          type: "list",
          allowBlank: false,
          formulae: [listFormulaByHeader["Student Status"]],
          showErrorMessage: true,
          errorTitle: "Invalid Student Status",
          error: "Select a Student Status from the dropdown list.",
        };
        if (columnMap["GPA"]) {
          templateSheet.getCell(
            `${columnMap["GPA"]}${rowNumber}`,
          ).dataValidation = {
            type: "decimal",
            operator: "between",
            allowBlank: false,
            formulae: [0, 4],
            showErrorMessage: true,
            errorTitle: "Invalid GPA",
            error: "GPA must be between 0 and 4.",
          };
        }
        templateSheet.getCell(
          `${columnMap["Cumulative GPA"]}${rowNumber}`,
        ).dataValidation = {
          type: "decimal",
          operator: "between",
          allowBlank: false,
          formulae: [0, 4],
          showErrorMessage: true,
          errorTitle: "Invalid GPA",
          error: "Cumulative GPA must be between 0 and 4.",
        };
      }

      const headerRow = templateSheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.alignment = { vertical: "middle", horizontal: "center" };

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const fileName = this.isGraduateEntryProgram
        ? "Graduate_Entry_Template.xlsx"
        : "TVET_Program_Template.xlsx";
      saveAs(blob, fileName);
      this.$toast.success(`${fileName} downloaded.`);
    },
    newImport() {
      this.showTable = false;
    },
    closeDialog() {
      this.detailDialog = false;
      this.addDialog = false;
      this.fetchImportedGraduates();
    },
    goBack() {
      this.showTable = true;
      this.showUpload = false;
      this.tableData = [];
      this.fileName = "";
      this.importErrors = [];
      this.importErrorsDialog = false;
      this.fetchImportedGraduates();
    },
    replaceImportFile() {
      this.showUpload = false;
      this.tableData = [];
      this.fileName = "";
      this.importErrors = [];
      this.importErrorsDialog = false;
    },
    async uploadGraduates() {
      if (this.tableData.length === 0) {
        this.$toast.error("No file detected, Please provide one!");
        return;
      }
      this.loading = true;
      try {
        let res = await GraduateService.importStructuredPreService(
          this.tableData,
        );
        if (res.status === 200) {
          this.showUpload = false;
          switch (res.data.status) {
            case 200:
              this.$toast.success(res.data.message);
              this.fileName = "";
              this.fetchImportedGraduates();
              break;
            case 500:
              this.$toast.error(res.data.message);
              break;
          }
        } else {
          this.showUpload = false;
          this.fetchImportedGraduates();
          this.$toast.error(
            "There was a problem uploading file, Please try Again!",
          );
        }
      } finally {
        this.loading = false;
      }
    },
    beforeUpload(file) {
      const isLt1M = file.size / 1024 / 1024 < 1;
      if (isLt1M) {
        return true;
      }
      this.$toast.warning("Please do not upload files larger than 1m in size.");
      return false;
    },
    handleSuccess({ header, results, fileName, sheets }) {
      const sourceValidation = validatePreserviceImportSource({
        fileName,
        headers: header,
        sheets,
        expectedHeaders: this.isGraduateEntryProgram
          ? DETAILED_STRUCTURED_TEMPLATE_HEADERS
          : TVET_TEMPLATE_HEADERS,
        expectedLegacyHeaders: this.isGraduateEntryProgram
          ? LEGACY_DETAILED_STRUCTURED_TEMPLATE_HEADERS
          : LEGACY_TVET_TEMPLATE_HEADERS,
        expectedTemplateSheetName: `${this.categoryLabel} Template`,
      });
      if (sourceValidation.hasErrors) {
        this.importErrors = sourceValidation.errors;
        this.importErrorsDialog = true;
        return;
      }

      const validation = validateStructuredPreServiceImport({
        rows: results,
        statusRows: sourceValidation.usesSeparateStatusSheet
          ? sheets?.[PRESERVICE_STATUS_SHEET_NAME]?.results || []
          : null,
        preServiceLevel: this.preServiceLevel,
        lookups: {
          backgroundProfessions: this.BACKGROUND_PROFESSIONS,
          disciplines: this.allDisciplines,
          educationalPrograms: this.EDUCATIONAL_PROGRAMS,
          ethnicities: this.ETHNICITIES,
          birthRegions: this.BIRTH_REGIONS,
          birthZones: this.BIRTH_ZONES,
          birthWoredas: this.BIRTH_WOREDAS,
          levelOfStudies: this.LEVEL_OF_STUDIES,
          studentStatuses: this.STUDENT_STATUSES,
          yearOfEducation: this.YEAR_OF_EDUCATION,
          universities: this.UNIVERSITIES,
        },
      });

      if (validation.hasErrors) {
        this.importErrors = validation.errors;
        this.importErrorsDialog = true;
        return;
      }
      this.importErrors = [];
      this.importErrorsDialog = false;
      this.showUpload = true;
      this.tableData = validation.validRows;
      this.fileName = fileName;
      this.$toast.success(
        `${validation.validRows.length} rows are ready to import.`,
      );
    },
    clearFilters() {
      this.name = null;
      this.idValue = null;
      this.programValue = null;
      this.disciplineValue = null;
      this.levelOfStudyValue = null;
      this.DISCIPLINES = this.allDisciplines;
      this.options.page = 1;
      this.fetchImportedGraduates();
    },
    viewImportedGraduates(item) {
      this.graduates = item;
      this.detailDialog = true;
      this.title = `${this.categoryLabel} Detail`;
    },
    addNimeiProgram() {
      this.graduates = null;
      this.title = `Add ${this.categoryLabel}`;
      this.addDialog = true;
    },
    editNimeiProgram(item) {
      this.graduates = item;
      this.title = `Edit ${this.categoryLabel}`;
      this.addDialog = true;
    },
    async registerOrUpdatePreService(graduates) {
      if (this.saving) return;
      this.saving = true;

      try {
        const res = graduates.editMode
          ? await GraduateService.updatePreService(graduates.id, graduates)
          : await GraduateService.createPreService(graduates);

        if (res?.data?.status !== 200) {
          this.$toast.error(
            res?.data?.message ||
              `Unable to ${graduates.editMode ? "update" : "create"} ${this.categoryLabel}, please try again`,
          );
          return;
        }

        this.$toast.success(res.data.message);
        this.addDialog = false;
        this.fetchImportedGraduates().catch((error) => {
          console.error(error);
          this.loading = false;
          this.$toast.error(
            "The entry was saved, but the list could not be refreshed. Please reload the page.",
          );
        });
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.message ||
            `Unable to ${graduates.editMode ? "update" : "create"} ${this.categoryLabel}, please try again`,
        );
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.selected {
  line-height: 16px;
  margin: 0 auto;
  font-size: 24px;
  border-radius: 5px;
  text-align: center;
  color: #bbb;
  position: relative;
}

.preservice-list-card {
  border-radius: 24px;
}

.filter-toolbar {
  margin-bottom: 16px;
  padding: 20px;
  border: 1px solid #dbe7f3;
  border-radius: 24px;
  background: linear-gradient(135deg, #f7fbff 0%, #eef6ff 52%, #ffffff 100%);
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.08);
}

.filter-toolbar__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.filter-toolbar__eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #2563eb;
}

.filter-toolbar__title {
  margin-top: 4px;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.15;
  color: #0f172a;
}

.filter-toolbar__actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.filter-toolbar__fields {
  margin: 0 !important;
}

.filter-toolbar__field {
  background: rgba(255, 255, 255, 0.9);
}

.filter-toolbar__primary-button,
.filter-toolbar__secondary-button,
.filter-toolbar__clear-button {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
}

.import-entry-shell {
  margin: 0 8px 24px;
}

.import-entry-card {
  padding: 20px;
  border: 1px solid #dbe7f3;
  border-radius: 24px;
  background: linear-gradient(145deg, #f8fbff 0%, #eef5ff 48%, #ffffff 100%);
  box-shadow: 0 20px 44px rgba(15, 23, 42, 0.08);
}

.import-entry-card__header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.import-entry-card__eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #2563eb;
}

.import-entry-card__title {
  margin-top: 4px;
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
}

.import-entry-card__subtitle {
  margin-top: 8px;
  max-width: 640px;
  color: #475569;
}

.import-entry-card__actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.import-entry-card__steps {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-top: 18px;
}

.import-entry-card__step {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.9);
  color: #334155;
}

.import-entry-card__step-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: #dbeafe;
  color: #1d4ed8;
  font-weight: 700;
}

.import-entry-card__uploader {
  margin-top: 20px;
}
</style>
